import { useEffect, useState } from 'react';
import { Clock, MessageCircle, X } from 'lucide-react';
import { useI18n } from '../../context/useI18n';
import { buildWaMeUrl } from '../../utils/whatsappSupport';
import { ADMIN_FORM_MODAL_PANEL } from './adminModalPanel';

const DAY_PRESETS = [3, 7, 15, 30];

export default function MarkPendingPaymentModal({
  open,
  user = null,
  saving = false,
  onClose,
  onConfirm,
}) {
  const { t } = useI18n();
  const [days, setDays] = useState('7');

  useEffect(() => {
    if (open) setDays('7');
  }, [open, user?.id]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  if (!open || !user) return null;

  const parsedDays = Number(days);
  const valid = Number.isFinite(parsedDays) && parsedDays >= 1 && parsedDays <= 365;
  const dueDate = new Date();
  dueDate.setDate(dueDate.getDate() + (valid ? parsedDays : 0));
  const waUrl = user.telefono
    ? buildWaMeUrl(
      user.telefono,
      `Hola ${user.nombre || ''}, te recordamos completar tu pago pendiente. Fecha límite: ${dueDate.toLocaleDateString('es-CO')}.`,
    )
    : null;

  return (
    <div
      className="form-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mark-pending-title"
      onClick={() => !saving && onClose?.()}
    >
      <div className={`${ADMIN_FORM_MODAL_PANEL} max-w-md`} onClick={(e) => e.stopPropagation()}>
        <div className="form-modal-header">
          <div className="flex items-center gap-2 min-w-0">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-amber-500/15 text-amber-700 shrink-0">
              <Clock size={18} />
            </span>
            <div className="min-w-0">
              <h4 id="mark-pending-title" className="font-semibold text-stone-900 truncate">
                {t('users.mark_pending_title', 'Marcar pago pendiente')}
              </h4>
              <p className="text-xs text-stone-500 truncate">{user.nombre}</p>
            </div>
          </div>
          <button
            type="button"
            className="form-close-btn"
            onClick={onClose}
            disabled={saving}
            aria-label={t('common.close', 'Cerrar')}
          >
            <X size={18} />
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!valid || saving) return;
            onConfirm?.(parsedDays);
          }}
          className="form-modal-content space-y-4"
        >
          <p className="text-sm text-stone-600">
            {t('users.mark_pending_hint', 'Indica cuántos días tendrá el usuario para completar el pago. Al llegar la fecha se enviará recordatorio y podrás contactarlo por WhatsApp.')}
          </p>

          <div>
            <label className="label" htmlFor="pending-days-input">
              {t('users.pending_days', 'Días para pagar')}
            </label>
            <div className="flex flex-wrap gap-2 mt-1">
              {DAY_PRESETS.map((preset) => {
                const selected = days === String(preset);
                return (
                  <button
                    key={preset}
                    type="button"
                    disabled={saving}
                    className={`min-w-[3rem] px-3 py-1.5 rounded-xl text-sm font-medium ${
                      selected ? 'btn-primary' : 'btn-secondary'
                    }`}
                    onClick={() => setDays(String(preset))}
                  >
                    {preset}d
                  </button>
                );
              })}
            </div>
            <input
              id="pending-days-input"
              type="number"
              min={1}
              max={365}
              className="input w-full mt-2"
              value={days}
              disabled={saving}
              onChange={(e) => setDays(e.target.value)}
            />
            {valid && (
              <p className="text-xs text-stone-500 mt-1">
                {t('users.pending_due', 'Fecha límite')}: {dueDate.toLocaleDateString('es-CO')}
              </p>
            )}
          </div>

          {waUrl ? (
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700 hover:text-emerald-900"
            >
              <MessageCircle size={16} />
              {t('users.preview_whatsapp', 'Vista previa mensaje WhatsApp')}
            </a>
          ) : (
            <p className="text-xs text-stone-500">
              {t('users.no_phone_whatsapp', 'Este usuario no tiene teléfono registrado para WhatsApp.')}
            </p>
          )}

          <div className="form-modal-footer -mx-5 -mb-5 mt-2">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
              {t('common.cancel', 'Cancelar')}
            </button>
            <button type="submit" className="btn-primary inline-flex items-center gap-2" disabled={!valid || saving}>
              {saving ? t('common.saving', 'Guardando...') : t('users.mark_pending_confirm', 'Marcar pendiente')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
